'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { unwrapApiData } from '@/lib/api-helpers'
import type {
  VoiceRealtimeConfigSummary,
  VoiceRealtimeSessionResponseData,
} from '@/types'
import {
  getLanguagesForProvider,
  getLanguageName,
} from '@shared/constants/languages'
import { getLanguageFlag } from '@shared/constants/flags'

interface FloatingVoiceWidgetProps {
  siteId: string
}

type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error'

interface LanguageOption {
  code: string
  name: string
  flag: string
}

export function FloatingVoiceWidget({
  siteId: _siteId,
}: FloatingVoiceWidgetProps) {
  const [status, setStatus] = useState<ConnectionStatus>('idle')
  const [selectedLanguage, setSelectedLanguage] = useState<string>('')
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null)
  const dataChannelRef = useRef<RTCDataChannel | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Fetch default bot first
  const botsQuery = useQuery({
    queryKey: ['bots', 'default'],
    queryFn: async () => {
      const response = await api.publicBots.getDefault()
      if (!response || !response.success) {
        return null
      }
      const botPayload: any = response?.data ?? null
      const botData: any = botPayload?.data ?? botPayload
      return botData || null
    },
  })

  const defaultBot = botsQuery.data

  // Fetch voice configs
  const configQuery = useQuery<VoiceRealtimeConfigSummary | null>({
    queryKey: [
      'voice.realtime.config.forBot',
      defaultBot?.id,
      defaultBot?.voiceRealtimeConfigId,
    ],
    queryFn: async () => {
      const response = await api.voice.realtime.listConfigs()
      if (!response || !response.success) {
        return null
      }
      const configs = unwrapApiData<VoiceRealtimeConfigSummary[]>(response)

      // If bot doesn't have voice config selected, don't show widget
      if (!defaultBot?.voiceRealtimeConfigId) {
        return null
      }

      // Find the bot's specific voice config
      const botConfig = configs?.find(
        c => c.id === defaultBot.voiceRealtimeConfigId && c.isActive
      )
      return botConfig || null
    },
    enabled: !!defaultBot, // Only fetch configs after bot is loaded
  })

  const config = configQuery.data
  const isLoading = configQuery.isLoading || botsQuery.isLoading

  // Convert language codes to options with flags
  const languageOptions = useMemo<LanguageOption[]>(() => {
    if (!config?.languages) {
      return []
    }

    const allLanguages = getLanguagesForProvider(config.provider)
    const options = config.languages
      .map(code => {
        const lang = allLanguages.find(l => l.code === code)
        return {
          code,
          name: lang?.name || code,
          flag: getLanguageFlag(config.provider, code),
        }
      })
      .filter(Boolean)

    return options
  }, [config])

  // Initialize selected language - always use admin's default on page load
  useEffect(() => {
    if (!config) return
    setSelectedLanguage(config.languageCode || '') // Always use admin default
  }, [config])

  // Handle language selection (no localStorage)
  const handleLanguageChange = useCallback((languageCode: string) => {
    setSelectedLanguage(languageCode)
    setShowLanguageDropdown(false)
  }, [])

  // Cleanup connections
  const cleanupConnections = useCallback(() => {
    dataChannelRef.current?.close()
    dataChannelRef.current = null

    if (peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach(sender => {
        sender.track?.stop()
      })
      peerConnectionRef.current.close()
      peerConnectionRef.current = null
    }

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop())
      localStreamRef.current = null
    }

    if (audioRef.current) {
      audioRef.current.srcObject = null
    }
  }, [])

  // Connect to voice assistant
  const handleConnect = useCallback(async () => {
    if (!config) return

    // Ensure bot is loaded before connecting
    if (!defaultBot) {
      console.error('[Voice Widget] Cannot connect: default bot not loaded')
      setError('Bot not loaded. Please refresh the page.')
      return
    }

    try {
      setStatus('connecting')
      setError(null)

      // Create session first
      const payload: any = {
        configId: config.id,
        botId: defaultBot.id, // Now guaranteed to exist
        languageCode: selectedLanguage,
      }

      const response = await api.voice.realtime.createSession(payload)
      if (!response || !response.success) {
        throw new Error(response?.error || 'Failed to create session')
      }

      const sessionData =
        unwrapApiData<VoiceRealtimeSessionResponseData>(response)
      if (!sessionData || !sessionData.clientSecret || !sessionData.webrtcUrl) {
        throw new Error('Session response incomplete')
      }

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      localStreamRef.current = stream

      // Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      })
      peerConnectionRef.current = pc

      // Add local audio track
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream)
      })

      // Handle remote audio
      pc.ontrack = event => {
        if (!audioRef.current) {
          audioRef.current = new Audio()
          audioRef.current.autoplay = true
        }
        audioRef.current.srcObject = event.streams[0]
      }

      // Handle connection state
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          setStatus('connected')
        } else if (
          pc.connectionState === 'failed' ||
          pc.connectionState === 'disconnected'
        ) {
          setStatus('error')
          setError('Connection failed')
          cleanupConnections()
        }
      }

      // Create data channel for events
      const dataChannel = pc.createDataChannel('oai-events')
      dataChannelRef.current = dataChannel

      // Track if we've sent the session update and greeting
      let sessionUpdateSent = false
      let greetingSent = false

      dataChannel.onmessage = async event => {
        try {
          const parsed = JSON.parse(event.data)

          // Log errors
          if (parsed.type === 'error') {
            console.error('[Voice Widget] OpenAI error:', parsed.error)
          }

          // Handle tool call requests from AI
          if (parsed?.type === 'response.function_call_arguments.done') {
            try {
              // Parse tool arguments
              const toolArgs = parsed.arguments
                ? JSON.parse(parsed.arguments)
                : {}

              // Execute tool via backend with botId and languageCode
              const result = await api.voice.realtime.executeTool({
                sessionId: sessionData.sessionId || 'unknown',
                toolName: parsed.name,
                toolArgs: toolArgs,
                botId: defaultBot.id,
                languageCode: selectedLanguage,
              })

              if (result.success) {
                // Send result back to AI via data channel
                const toolResultMessage = {
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: parsed.call_id,
                    output:
                      typeof result.data === 'string'
                        ? result.data // Already a string, don't stringify again
                        : JSON.stringify(result.data), // Convert non-string to JSON string
                  },
                }

                if (dataChannel && dataChannel.readyState === 'open') {
                  try {
                    dataChannel.send(JSON.stringify(toolResultMessage))

                    // After sending tool result, request AI to generate a response with audio
                    dataChannel.send(
                      JSON.stringify({
                        type: 'response.create',
                        response: {
                          modalities: ['audio', 'text'],
                        },
                      })
                    )
                  } catch (sendErr) {
                    console.error(
                      '[Voice Widget] Failed to send tool result:',
                      sendErr
                    )
                  }
                } else {
                  console.error(
                    '[Voice Widget] DataChannel not open, cannot send tool result'
                  )
                }
              } else {
                console.error(
                  '[Voice Widget] Tool execution failed:',
                  result.error
                )
                // Send error back to AI
                if (dataChannel && dataChannel.readyState === 'open') {
                  try {
                    dataChannel.send(
                      JSON.stringify({
                        type: 'conversation.item.create',
                        item: {
                          type: 'function_call_output',
                          call_id: parsed.call_id,
                          output: JSON.stringify({
                            error: result.error || 'Tool execution failed',
                          }),
                        },
                      })
                    )
                  } catch (sendErr) {
                    console.error(
                      '[Voice Widget] Failed to send error:',
                      sendErr
                    )
                  }
                }
              }
            } catch (err) {
              console.error('[Voice Widget] Tool execution error:', err)
              // Send error back to AI
              if (dataChannel && dataChannel.readyState === 'open') {
                try {
                  dataChannel.send(
                    JSON.stringify({
                      type: 'conversation.item.create',
                      item: {
                        type: 'function_call_output',
                        call_id: parsed.call_id,
                        output: JSON.stringify({
                          error:
                            err instanceof Error
                              ? err.message
                              : 'Tool execution failed',
                        }),
                      },
                    })
                  )
                } catch (sendErr) {
                  console.error(
                    '[Voice Widget] Failed to send error to AI:',
                    sendErr
                  )
                }
              }
            }
          }

          // Wait for session.created before sending session.update
          if (parsed?.type === 'session.created' && !sessionUpdateSent) {
            sessionUpdateSent = true

            const sessionUpdate: any = {}

            // CRITICAL: Set input_audio_transcription for Whisper
            // The WebRTC session doesn't inherit the REST API session config, so we must set it here
            if (selectedLanguage) {
              const langCode = selectedLanguage.split('-')[0] // Extract base language code (e.g., "en-US" -> "en")
              sessionUpdate.input_audio_transcription = {
                model: 'whisper-1',
                language: langCode,
              }
            }

            // Add instructions if available (CRITICAL for language!)
            if (sessionData.instructions) {
              sessionUpdate.instructions = sessionData.instructions
            }

            // Add tools if available
            if (sessionData.tools && sessionData.tools.length > 0) {
              // Convert tools from REST API format to WebSocket format
              sessionUpdate.tools = sessionData.tools.map((tool: any) => ({
                type: 'function',
                name: tool.function.name,
                description: tool.function.description,
                parameters: tool.function.parameters,
              }))
            }

            if (Object.keys(sessionUpdate).length > 0) {
              const updateMessage = {
                type: 'session.update',
                session: sessionUpdate,
              }
              dataChannel.send(JSON.stringify(updateMessage))
            }
          }

          // Check if session was updated - send initial greeting (only once)
          if (parsed?.type === 'session.updated' && !greetingSent) {
            greetingSent = true
            if (sessionData.initialGreeting) {
              // Send response.create with greeting in instructions
              // Include language directive to maintain language consistency
              // Use the same logic as backend: getLanguageName for dynamic language support
              const langCode = selectedLanguage.split('-')[0] // Extract base language code (e.g., "en-US" -> "en")
              const languageName = getLanguageName(config.provider, langCode)
              const languageDirective = languageName
                ? `Respond in ${languageName} language.`
                : 'Respond in English language.' // Fallback to English if language not found

              dataChannel.send(
                JSON.stringify({
                  type: 'response.create',
                  response: {
                    modalities: ['text', 'audio'],
                    instructions: `${languageDirective}\n\nSpeak this exact greeting and nothing else: "${sessionData.initialGreeting}"`,
                  },
                })
              )
            }
          }
        } catch (err) {
          console.error('Data channel message error:', err)
        }
      }

      // Create offer
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
      })
      await pc.setLocalDescription(offer)

      // Negotiate with backend
      const headers: Record<string, string> = {
        'Content-Type': 'application/sdp',
        Authorization: `Bearer ${sessionData.clientSecret}`,
      }

      if (sessionData.provider === 'openai') {
        headers['OpenAI-Beta'] = 'realtime=v1'
      }

      const negotiateResponse = await fetch(sessionData.webrtcUrl, {
        method: 'POST',
        headers,
        body: offer.sdp ?? '',
      })

      if (!negotiateResponse.ok) {
        throw new Error(
          `WebRTC negotiation failed (${negotiateResponse.status})`
        )
      }

      const answerSdp = await negotiateResponse.text()
      await pc.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      })

      setStatus('connected')
    } catch (err) {
      console.error('Failed to connect:', err)
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Failed to connect')
      cleanupConnections()
    }
  }, [config, defaultBot, selectedLanguage, cleanupConnections])

  // Disconnect
  const handleDisconnect = useCallback(() => {
    cleanupConnections()
    setStatus('idle')
  }, [cleanupConnections])

  // Toggle connection
  const handleToggle = useCallback(() => {
    if (status === 'idle') {
      handleConnect()
    } else if (status === 'connected') {
      handleDisconnect()
    }
  }, [status, handleConnect, handleDisconnect])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-voice-widget]')) {
        setShowLanguageDropdown(false)
      }
    }

    if (showLanguageDropdown) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showLanguageDropdown])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupConnections()
    }
  }, [cleanupConnections])

  // Don't render if still loading
  if (isLoading) {
    return null
  }

  // Don't render if no bot
  if (!defaultBot) {
    console.warn('[Voice Widget] No default bot found, widget disabled')
    return null
  }

  // Don't render if bot doesn't have voice config selected
  if (!defaultBot.voiceRealtimeConfigId) {
    console.warn(
      '[Voice Widget] Bot has no voice configuration selected, widget disabled'
    )
    return null
  }

  // Don't render if voice config not found or not active
  if (!config) {
    console.warn(
      '[Voice Widget] Voice configuration not found or not active, widget disabled'
    )
    return null
  }

  const buttonText =
    status === 'connected'
      ? 'End Call'
      : status === 'connecting'
        ? 'Connecting...'
        : 'Call AI Agent'

  const selectedFlag = config
    ? getLanguageFlag(config.provider, selectedLanguage)
    : '🌐'

  return (
    <div className="fixed bottom-4 left-4 z-50" data-voice-widget>
      {/* Error message */}
      {error && status === 'error' && (
        <div className="absolute bottom-20 left-0 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg max-w-xs mb-2">
          {error}
        </div>
      )}

      {/* Language dropdown */}
      {showLanguageDropdown && (
        <div className="absolute bottom-20 left-0 bg-white rounded-xl shadow-xl border border-gray-200 p-2 min-w-[200px] max-h-[300px] overflow-y-auto z-[60]">
          {languageOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No languages available
            </div>
          ) : (
            languageOptions.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  selectedLanguage === lang.code
                    ? 'bg-blue-50 text-blue-600 font-semibold'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <span className="text-xl">{lang.flag}</span>
                <span className="flex-1 text-left text-sm">{lang.name}</span>
                {selectedLanguage === lang.code && (
                  <span className="text-blue-600">✓</span>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {/* Button container */}
      <div className="flex items-center gap-2">
        {/* Call button */}
        <button
          onClick={handleToggle}
          disabled={status === 'connecting'}
          className={`flex items-center gap-3 px-5 py-3 rounded-full font-semibold shadow-lg transition-all ${
            status === 'connected'
              ? 'bg-red-500 hover:bg-red-600 hover:shadow-xl'
              : status === 'connecting'
                ? 'bg-orange-500'
                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl hover:-translate-y-0.5'
          } text-white`}
        >
          {/* Waveform icon */}
          <div className="flex items-center gap-0.5">
            {[12, 20, 16, 24, 18].map((height, i) => (
              <div
                key={i}
                className="w-0.5 bg-white rounded-full transition-all"
                style={{
                  height: status === 'connected' ? `${height}px` : '12px',
                  animation:
                    status === 'connected'
                      ? `pulse ${0.8 + i * 0.1}s ease-in-out infinite`
                      : 'none',
                }}
              />
            ))}
          </div>

          {/* Button text */}
          <span>{buttonText}</span>
        </button>

        {/* Language selector button (separate) */}
        {languageOptions.length > 1 && (
          <button
            onClick={e => {
              e.stopPropagation()
              setShowLanguageDropdown(!showLanguageDropdown)
            }}
            className="w-12 h-12 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-full text-2xl shadow-lg transition-all hover:scale-105"
          >
            {selectedFlag}
          </button>
        )}
      </div>
    </div>
  )
}
